import helpContent from './helpContent.json';

const WILDCARD_SEGMENT = '*';

const trimTrailingSlash = path =>
  path.length > 1 && path.endsWith('/') ? path.slice(0, -1) : path;

const splitPathSegments = path => {
  const normalized = trimTrailingSlash(path);
  if (!normalized || normalized === '/') {
    return [];
  }
  return normalized.split('/').filter(Boolean);
};

/**
 * Match a pathname against a pattern where `*` matches exactly one path segment.
 */
export const pathMatchesPattern = (pathname, pattern) => {
  const pathSegments = splitPathSegments(pathname);
  const patternSegments = splitPathSegments(pattern);

  if (pathSegments.length !== patternSegments.length) {
    return false;
  }

  return patternSegments.every(
    (patternSegment, index) =>
      patternSegment === WILDCARD_SEGMENT || patternSegment === pathSegments[index]
  );
};

const wildcardCount = pattern =>
  splitPathSegments(pattern).filter(segment => segment === WILDCARD_SEGMENT).length;

/**
 * All help topics in authoring order.
 * @returns {Array<{id: string, title: string, paths?: string[], blocks: Array}>}
 */
export const getAllTopics = () => helpContent.topics;

/**
 * Find a topic by its id.
 */
export const getTopicById = topicId => {
  if (!topicId) {
    return null;
  }
  return helpContent.topics.find(topic => topic.id === topicId) || null;
};

/**
 * Find the topic mapped to a URL pathname.
 * Supports exact paths and `*` wildcards (one segment each). Trailing slashes are ignored.
 * When multiple patterns match, the most specific pattern wins (fewest wildcards).
 */
export const getTopicByPath = pathname => {
  if (!pathname) {
    return null;
  }
  const path = trimTrailingSlash(pathname);

  let bestMatch = null;
  let bestWildcardCount = Infinity;

  for (const topic of helpContent.topics) {
    for (const topicPath of topic.paths || []) {
      const normalizedPattern = trimTrailingSlash(topicPath);
      if (!pathMatchesPattern(path, normalizedPattern)) {
        continue;
      }

      const wildcards = wildcardCount(normalizedPattern);
      if (wildcards < bestWildcardCount) {
        bestMatch = topic;
        bestWildcardCount = wildcards;
      }
    }
  }

  return bestMatch;
};
